import { useEffect, useCallback } from "react";
import { usePostMessage } from "./usePostMessage";
import { createLogoutHandler } from "@/services/postMessage/handlers/LogoutHandler";
import { LogoutResponseMessage, ReadyMessage } from "@/services/postMessage/types";
import { getEmbedModeFlag } from "@/utils/tokenManager";

export interface UseEmbedLogoutOptions {
  allowedOrigins?: string[];
  onSuccess?: () => void;
  onError?: (error: string) => void;
  redirectToEmbed?: boolean;
  onUserChange?: (user: undefined) => void;
}

export const useEmbedLogout = (options: UseEmbedLogoutOptions = {}) => {
  const isEmbedMode = getEmbedModeFlag();

  const service = usePostMessage({
    allowedOrigins: options.allowedOrigins,
    enabled: isEmbedMode,
  });

  const sendResponse = useCallback(
    (message: LogoutResponseMessage) => {
      if (!service) {
        console.warn("[useEmbedLogout] Service not ready, cannot send response");
        return;
      }

      service.send(message);
    },
    [service]
  );

  const sendReady = useCallback(() => {
    if (!service) {
      console.warn("[useEmbedLogout] Service not ready, cannot send READY");
      return;
    }

    const message: ReadyMessage = {
      type: "READY",
      timestamp: Date.now(),
    };

    console.log("[useEmbedLogout] Sending READY message");
    service.send(message);
  }, [service]);

  useEffect(() => {
    if (!isEmbedMode || !service) {
      if (!isEmbedMode) {
        console.log("[useEmbedLogout] Not in embed mode, skipping LOGOUT handler");
      }
      return;
    }

    console.log("[useEmbedLogout] Setting up global LOGOUT handler");

    const logoutHandler = createLogoutHandler({
      sendResponse,
      redirectToEmbed: options.redirectToEmbed,
      onSuccess: () => {
        console.log("[useEmbedLogout] Logout successful");
        if (options.onUserChange) {
          options.onUserChange(undefined);
        }
        if (options.onSuccess) {
          options.onSuccess();
        }
        sendReady();
      },
      onError: (errorMsg) => {
        console.error("[useEmbedLogout] Logout failed", errorMsg);
        if (options.onError) {
          options.onError(errorMsg);
        }
      },
    });

    const unsubscribe = service.subscribe("LOGOUT", logoutHandler);

    return () => {
      console.log("[useEmbedLogout] Cleaning up LOGOUT handler");
      unsubscribe();
    };
  }, [isEmbedMode, service, options, sendResponse, sendReady]);

  return {
    isEnabled: isEmbedMode,
    isActive: service?.isActive() ?? false,
  };
};
